import { UsbAccessDeniedModal } from '@/app-shell/chronicle/components/usb-access-denied-modal';
import {
  useIsDictateDeviceSetupVisible,
  useIsDictateHardwareMicEnabled,
} from '@/app-shell/flags/dictate-flags';
import { useIsDocxFormEnabled } from '@/app-shell/flags/growth-flags';
import { usbDictaphoneSetupCardQueueItemAtom } from '@/atoms/launchModalsConfigAtom';
import { JsonFormReviewModal } from '@/components/modal/form-filling-modals/lazy-json-form-modals';
import { snippetModalOpenAtom } from '@/components/modal/snippet-editor-modal-atoms';
import { listAddDevices } from '@/composition/hosts/dictation/device-setup-add/list-devices';
import { useMappedButtonListeners } from '@/composition/hosts/dictation/mapped-actions/use-mapped-button-listeners';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import {
  listenHardwareMicButtonPressed,
  setButtonCaptureMode,
  setDeviceButtonMappings,
} from '@/tauri/dictation/api';
import { isOnPrem } from '@/utils/buildProfile';
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { logger } from '@/utils/logger/logger';
import { USB_DICTAPHONE_SETUP_CARD_V2_MODAL_ID } from '@heidi/dictate';
import {
  dictateMapFunctionalityDialogRequestAtom,
  hardwareMicDeviceKey,
  hardwareMicDevicesAtom,
  useDictaphoneSetupAwarenessTrigger,
  type DeviceButtonMappings,
} from '@heidi/dictate/data';
import { useIsXfaPdfFormEnabled } from '@heidi/growth/flags';
import { sanitizeError } from '@heidi/runtime/logger';
import { isCreateTemplateModalOpenAtom } from '@heidi/templates';
import {
  chooseTemplateTypeModalOpenAtom,
  formFillingUploadModalOpenAtom,
  getPdfNonFillableMaxPages,
  jsonFormReviewModalOpenAtom,
  xmlFormReviewModalOpenAtom,
} from '@heidi/templates/data';
import { useAtomValue } from 'jotai';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, type ComponentType } from 'react';

import AutoLogout from './AutoLogout';
// Resolved as `.web` or `.tauri` by next.config.js so web builds never import
// desktop-only modal code.
import { GlobalTauriModals } from './GlobalTauriModals';
import {
  HcpVerificationModal,
  HcpVerificationNotificationSource,
} from './global-modals-hcp-verification';
import { ModalLaunchCoreStateSync } from './modal-launch-core-state-sync';
import { ModalPacingSync } from './modal-pacing-sync';
import { ModalTierFrameworkSync } from './modal-tier-framework-sync';
import { useDictaphoneTriggerModeSync } from './use-dictaphone-trigger-mode-sync';
import { useTimeToLogout } from './use-time-to-logout';

const SnippetModal = dynamic(() => import('@/components/modal/snippet-editor-modal'), {
  ssr: false,
  loading: () => null,
});

const DeviceSetupAwarenessPopup = dynamic(
  () => import('@heidi/dictate').then((mod) => mod.DeviceSetupAwarenessPopup),
  { ssr: false, loading: () => null }
);

const AddDeviceDialog = dynamic(
  () => import('@heidi/dictate').then((mod) => mod.AddDeviceDialog),
  { ssr: false, loading: () => null }
);

const MapFunctionalityDialogHost = dynamic(
  () => import('@heidi/dictate').then((mod) => mod.MapFunctionalityDialogHost),
  { ssr: false, loading: () => null }
);

const syncMapFunctionalityMappings = (next: DeviceButtonMappings) => {
  void Promise.resolve(setDeviceButtonMappings(next)).catch((error: unknown) => {
    logger.error({
      event_type: 'DICTATION_DEVICE_BUTTON_MAPPINGS_SYNC_FAILED',
      details: { error: sanitizeError(error) },
    });
  });
};

const CreateTemplateModal = dynamic(
  () =>
    import('@/composition/hosts/templates/create-template/create-template-host').then(
      (mod) => ({ default: mod.CreateTemplateModalHost })
    ),
  { ssr: false, loading: () => null }
);

const SettingsModalShell = dynamic(
  () =>
    import('@/app-shell/settings/components/SettingsModal').then((mod) => ({
      default: mod.SettingsModalShell,
    })),
  { ssr: false, loading: () => null }
);

const RequireTeamMemberSpecialtyModal = dynamic(
  () =>
    import('@/composition/hosts/workspace/require-team-member-specialty-modal-host').then(
      (mod) => ({ default: mod.RequireTeamMemberSpecialtyModalHost })
    ),
  { ssr: false, loading: () => null }
);

// Template creation. On-prem the dropzone narrows to XML (the appliance's
// gateway is text-only and cannot fill a PDF form), and the two XML modals are
// the whole form flow there.
const ChooseTemplateTypeModal = dynamic(
  () => import('@/components/modal/form-filling-modals'),
  { ssr: false, loading: () => null }
);

const FormFillingUploadModal = dynamic<{
  isDocxFormEnabled: boolean;
  isXfaPdfFormEnabled: boolean;
  isHeidiSupportReachable: boolean;
  pdfNonFillableMaxPages: number;
}>(
  () =>
    import('@heidi/templates').then((mod) => ({
      default: mod.FormFillingUploadModal,
    })),
  { ssr: false, loading: () => null }
);

const XmlFormReviewModal = dynamic(
  () =>
    import('@/components/modal/form-filling-modals/xml-form-review/xml-form-review-modal'),
  { ssr: false, loading: () => null }
);

/**
 * Platform-level E2E exemption for global modals.
 *
 * `__SUPPRESS_GLOBAL_MODALS__` hides the whole global-modal tree so
 * announcement modals never intercept E2E test clicks. A modal that is
 * itself a step under test opts back in by adding one entry to
 * `e2eExemptModals` below. Pure: `markNotSuppressedInE2E` only tags the
 * component; the list is a declarative literal, never mutated.
 */
type E2EExemptEntry = { Component: ComponentType };

function markNotSuppressedInE2E<P>(Component: ComponentType<P>): E2EExemptEntry {
  return { Component: Component as ComponentType };
}

const e2eExemptModals: E2EExemptEntry[] = [
  markNotSuppressedInE2E(ChooseTemplateTypeModal),
];

/** Rendered by `GlobalModals` INSTEAD of the full tree while suppressed. */
export const E2EExemptGlobalModals = () => {
  return (
    <>
      {e2eExemptModals.map(({ Component }, index) => (
        <Component key={index} />
      ))}
    </>
  );
};

const isTauriBuild = process.env.NEXT_PUBLIC_IS_TAURI === 'true';

/**
 * Every global modal that is meaningful on every build — SaaS web, desktop,
 * and on-prem ("Vault") — mounted by `GlobalModals` before it decides whether
 * to also mount the cloud-only tree.
 *
 * **This is the default home for a new global modal.** Put it here unless it
 * reads cloud billing, subscription, trial or entitlement state; only then does
 * it belong in `CloudOnlyGlobalModals`. Getting that call wrong in this
 * direction shows a cloud surface on an appliance — visible in QA. Getting it
 * wrong in the other direction is silent: the trigger sets its atom and nothing
 * renders, which is the F6 failure in `onprem-guardrails.md`. It has already
 * cost the snippet modal, the create-template modal, the settings modal and
 * then everything below the shared four.
 *
 * Keep the imports here free of cloud-only modules so the split stays real once
 * `isOnPrem` actually dead-code-eliminates.
 */
export const UniversalGlobalModals = ({
  isTimerRunning = false,
}: {
  isTimerRunning?: boolean;
}) => {
  const snippetModalOpen = useAtomValue(snippetModalOpenAtom);
  const createTemplateModalOpen = useAtomValue(isCreateTemplateModalOpenAtom);
  const chooseTemplateTypeModalOpen = useAtomValue(chooseTemplateTypeModalOpenAtom);
  const formFillingUploadModalOpen = useAtomValue(formFillingUploadModalOpenAtom);
  const isDocxFormEnabled = useIsDocxFormEnabled();
  const isXfaPdfFormEnabled = useIsXfaPdfFormEnabled();
  const xmlFormReviewModalOpen = useAtomValue(xmlFormReviewModalOpenAtom);
  const jsonFormReviewModalOpen = useAtomValue(jsonFormReviewModalOpenAtom);
  const timeToLogout = useTimeToLogout();
  // Arms the dictaphone setup awareness card. Always hosted here so the
  // trigger lives on every build; either device phase flag gates arming, and
  // phase 2 picks the setup variant. The hardware snapshot and the composed
  // flag gate are host signals the package hook takes as ports.
  const hardwareMicDevices = useAtomValue(hardwareMicDevicesAtom);
  const hardwareMicEnabled = useIsDictateHardwareMicEnabled();
  // Live gate for the kill switches below (mapping capture, button listeners):
  // they must take effect on the render the flag flips.
  const deviceSetupOffered = useIsDictateDeviceSetupVisible();
  // The card's id, recorded phase and "Set up now" all come from the atom the
  // launch row enqueues from, so they cannot disagree with the tracked id while
  // the sync effect catches up to a flag change.
  const deviceSetupCardModalId = useAtomValue(usbDictaphoneSetupCardQueueItemAtom).id;
  const deviceSetupCardIsPhase2 =
    deviceSetupCardModalId === USB_DICTAPHONE_SETUP_CARD_V2_MODAL_ID;
  const deviceKeys = useMemo(
    () => hardwareMicDevices.map(hardwareMicDeviceKey),
    [hardwareMicDevices]
  );
  useDictaphoneSetupAwarenessTrigger({
    deviceKeys,
    visible: hardwareMicEnabled,
    phase: deviceSetupCardIsPhase2 ? 'setup' : 'detected',
  });

  // One Tauri listener for the map dialog's life; the dialog registers its
  // capture handler through this and filters to the open device itself.
  const subscribeButtonPress = useCallback(
    (handler: (press: { deviceKey: string; buttonId: string }) => void) => {
      let unlisten: (() => void) | null = null;
      let active = true;
      void listenHardwareMicButtonPressed((event) => {
        if (active) handler(event.payload);
      }).then((fn) => {
        unlisten = fn;
      });
      return () => {
        active = false;
        unlisten?.();
      };
    },
    []
  );

  // While the map dialog is open, device presses capture instead of
  // triggering dictation. Always push the current value: a reload while the
  // dialog was open skips the cleanup, and Rust would stay in capture mode.
  const mapFunctionalityRequest = useAtomValue(dictateMapFunctionalityDialogRequestAtom);
  const mapDialogOpen = deviceSetupOffered && mapFunctionalityRequest !== null;
  useEffect(() => {
    void setButtonCaptureMode(mapDialogOpen);
    return () => {
      void setButtonCaptureMode(false);
    };
  }, [mapDialogOpen]);
  useDictaphoneTriggerModeSync(hardwareMicEnabled);
  useMappedButtonListeners(deviceSetupOffered);
  const { isOpen: deviceSetupPopupOpen, closeWithOptions: closeDeviceSetupPopup } =
    useQueuedModal(deviceSetupCardModalId);

  return (
    <>
      {/* Queue infrastructure: both mirror a kill switch into the atom the
          modal queue reads, so a build without them runs the queue on stale
          defaults. */}
      <ModalPacingSync />
      <ModalTierFrameworkSync />
      {/* Core queue state (auth/user/kinde/recording) for the on-prem build —
          the only build where the cloud-only writer is unmounted. Without it
          the on-prem launch queue stays blocked and nothing below ever opens;
          on SaaS the cloud-only writer covers the same fields, so a second
          set of observers and writes here would be pure duplication. */}
      {isOnPrem && <ModalLaunchCoreStateSync isTimerRunning={isTimerRunning} />}

      {/* Desktop recovery surfaces (mic lost, capture silent, WAV writer
          failed, reload-while-recording). `GlobalTauriModals` resolves to a
          null component on web, and on-prem ships a Tauri build. */}
      {isTauriBuild && <GlobalTauriModals />}

      {/* `key` remounts on a timeout change, which is load-bearing: `useIdle`
          re-arms its timer when the duration changes but does NOT clear an
          already-latched `idle`. Without the remount, a user who had gone idle
          under a short timeout and then raised it would be logged out on the
          next render instead of getting the longer window. */}
      {timeToLogout !== null && (
        <AutoLogout key={timeToLogout} timeToLogout={timeToLogout} />
      )}

      <UsbAccessDeniedModal />

      {/* Queue-managed: the modal queue owns visibility via `useQueuedModal`;
        mounted unconditionally — no flag or `isTauriBuild` wrap (the trigger
        and the launch row gate eligibility). */}
      <DeviceSetupAwarenessPopup
        isOpen={deviceSetupPopupOpen}
        closeWithOptions={closeDeviceSetupPopup}
        setupOffered={deviceSetupCardIsPhase2}
      />

      {/* Atom-gated. The Settings Dictaphone row is the flagged entry
          (`release-scribe-dictate-device-setup`); the dialog itself is not,
          so a row click can always open it. */}
      <AddDeviceDialog
        visible={isTauriBuild}
        listDevices={listAddDevices}
        connectedDeviceKeys={deviceKeys}
      />

      {/* Atom-gated. The shell owns the flag and the native sync port. */}
      <MapFunctionalityDialogHost
        visible={deviceSetupOffered}
        devices={hardwareMicDevices.map((device) => ({
          key: hardwareMicDeviceKey(device),
          model: device.model,
        }))}
        onSyncMappings={syncMapFunctionalityMappings}
        subscribeButtonPress={subscribeButtonPress}
      />

      {snippetModalOpen && <SnippetModal />}
      {createTemplateModalOpen && <CreateTemplateModal />}
      {chooseTemplateTypeModalOpen && <ChooseTemplateTypeModal />}
      {formFillingUploadModalOpen && (
        <FormFillingUploadModal
          isDocxFormEnabled={isDocxFormEnabled}
          isXfaPdfFormEnabled={isXfaPdfFormEnabled}
          isHeidiSupportReachable={isHeidiSupportReachable}
          pdfNonFillableMaxPages={getPdfNonFillableMaxPages(
            process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
          )}
        />
      )}
      {xmlFormReviewModalOpen && <XmlFormReviewModal />}
      {jsonFormReviewModalOpen && <JsonFormReviewModal />}
      <RequireTeamMemberSpecialtyModal />
      <SettingsModalShell />
      {/* Queue-driven shell; its form loads lazily on first open. Inert
          on-prem: the backend gate answers 403 there, so neither the settings
          card nor the badge probe can open it. */}
      <HcpVerificationModal />
      {/* Headless, flag-gated badge read on app load: the backend writes the
          daily verification reminder inmail as a side effect of it. Lives
          with the modal so the two share one host and one flag gate. */}
      <HcpVerificationNotificationSource />
    </>
  );
};
